import type { PdfRect } from "./pdfBearbeitung";

/**
 * Bilder im PDF-Editor (User 04.10.2026): „Bilder auch im PDF-Editor anzeigen
 * und damit auch bearbeiten können, sind bzw. bleiben dann Bilder, kann man
 * aber auch als PDF konvertieren“.
 *
 * Der Weg: Das Bild wird für die Sitzung im Browser in ein Einseiten-PDF
 * gehüllt (`bildSitzung.ts`), mit dem vorhandenen Editor bearbeitet und beim
 * Speichern wieder als Bild gerastert — oder als PDF gespeichert.
 *
 * Hier steht nur die reine Rechnung dazu (DOM-frei, im node-Runner prüfbar).
 * Die Serverseite spiegelt die Format-Regeln in
 * `apps/api/src/document-editing/bild-format.util.ts`.
 */

export type DateiArt = "pdf" | "jpeg" | "png" | "webp" | "gif" | "bmp";
export type BildArt = Exclude<DateiArt, "pdf">;

const beginntMit = (b: Uint8Array, bytes: readonly number[], ab = 0) =>
    b.length >= ab + bytes.length && bytes.every((v, i) => b[ab + i] === v);
const ascii = (s: string) => Array.from(s, (c) => c.charCodeAt(0));

/**
 * Was ist das für eine Datei? Maßgeblich ist die Signatur, nicht Name oder
 * MIME-Typ — nach „Als PDF speichern“ trägt der Aufrufer noch den alten Namen,
 * die Bytes sind aber schon ein PDF. `null` = unbekannt.
 */
export function erkenneDateiart(kopf: Uint8Array): DateiArt | null {
    if (beginntMit(kopf, [0xff, 0xd8, 0xff])) return "jpeg";
    if (beginntMit(kopf, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return "png";
    if (beginntMit(kopf, ascii("RIFF")) && beginntMit(kopf, ascii("WEBP"), 8)) return "webp";
    if (beginntMit(kopf, ascii("GIF87a")) || beginntMit(kopf, ascii("GIF89a"))) return "gif";
    if (beginntMit(kopf, ascii("BM"))) return "bmp";
    // PDF.js duldet bis zu 1024 Byte Vorspann vor dem Header (Mail/Scanner).
    const marke = ascii("%PDF-");
    for (let i = 0; i + marke.length <= Math.min(kopf.length, 1024); i++) if (beginntMit(kopf, marke, i)) return "pdf";
    return null;
}

export interface ZielFormat {
    mime: "image/jpeg" | "image/png" | "image/webp";
    /** Bevorzugte Endung ohne Punkt. */
    endung: string;
    /** Endungen, die zu diesem Format passen (bleiben beim Speichern stehen). */
    endungen: readonly string[];
    /** So heißt das Format für den Nutzer. */
    anzeige: string;
}

const JPEG: ZielFormat = { mime: "image/jpeg", endung: "jpg", endungen: ["jpg", "jpeg"], anzeige: "JPG" };
const PNG: ZielFormat = { mime: "image/png", endung: "png", endungen: ["png"], anzeige: "PNG" };
const WEBP: ZielFormat = { mime: "image/webp", endung: "webp", endungen: ["webp"], anzeige: "WebP" };

/**
 * In welchem Format wird das bearbeitete Bild geschrieben? JPEG, PNG und WebP
 * bleiben, was sie sind. GIF und BMP kann der Browser nicht schreiben — sie
 * werden zu PNG (verlustfrei), die Endung zieht mit.
 */
export function zielFormat(art: BildArt): ZielFormat {
    if (art === "jpeg") return JPEG;
    if (art === "webp") return WEBP;
    return PNG;
}

/** Endung des Basenames ohne Punkt, klein — `""`, wenn es keine gibt. */
export function endungVon(fileName: string): string {
    const base = fileName.slice(fileName.lastIndexOf("/") + 1);
    const punkt = base.lastIndexOf(".");
    return punkt > 0 && punkt < base.length - 1 ? base.slice(punkt + 1).toLowerCase() : "";
}

/** Setzt die Endung (ohne Punkt); eine schon passende bleibt samt Schreibweise stehen. */
export function mitEndung(fileName: string, endung: string): string {
    const slash = fileName.lastIndexOf("/");
    const dir = fileName.slice(0, slash + 1);
    const base = fileName.slice(slash + 1);
    const alt = endungVon(fileName);
    if (alt === endung.toLowerCase()) return fileName;
    const stamm = alt ? base.slice(0, base.length - alt.length - 1) : base;
    return `${dir}${stamm}.${endung}`;
}

/**
 * Der Dateiname, wie er nach dem Speichern heißt: als PDF mit „.pdf“, als Bild
 * mit der Endung des Bestands — außer das Format wechselt (GIF/BMP → PNG).
 */
export function nameFuerZiel(name: string, bestand: string, ziel: ZielFormat, als: "bild" | "pdf"): string {
    if (als === "pdf") return mitEndung(name, "pdf");
    const alt = endungVon(bestand);
    // Schreibweise des Bestands übernehmen („Foto.JPG“ bleibt „.JPG“).
    const roh = bestand.slice(bestand.length - alt.length);
    return mitEndung(name, ziel.endungen.includes(alt) ? roh : ziel.endung);
}

/** A4 in pt — die Bildseite wird darauf eingepasst. */
const A4_KURZ = 595.28;
const A4_LANG = 841.89;

/**
 * Seitengröße (pt) für ein Bild von `breitePx` × `hoehePx`.
 *
 * Große Fotos werden auf A4 eingepasst (hoch oder quer, je nach Bild), statt
 * 1 px = 1 pt zu rechnen: Stempel, Unterschriften und die Schriftgröße neuer
 * Texte sind in pt bemessen und wären auf einer 4000 pt breiten Seite winzig.
 * Die Pixel bleiben davon unberührt — gerastert wird beim Speichern wieder in
 * der Originalauflösung. Kleine Bilder werden nicht aufgeblasen.
 */
export function seitenMass(breitePx: number, hoehePx: number): { breitePt: number; hoehePt: number } {
    const quer = breitePx > hoehePx;
    const faktor = Math.min(1, (quer ? A4_LANG : A4_KURZ) / breitePx, (quer ? A4_KURZ : A4_LANG) / hoehePx);
    const runde = (v: number) => Math.round(v * 100) / 100;
    return { breitePt: runde(breitePx * faktor), hoehePt: runde(hoehePx * faktor) };
}

/** Größtes Bild, das der Editor annimmt (Pixel) — darüber wird das Canvas des Browsers unzuverlässig. */
export const MAX_BILD_PIXEL = 64_000_000;
/** Längste Kante in Pixeln. */
export const MAX_BILD_KANTE = 16_000;

/**
 * EXIF-Ausrichtung eines JPEG (1–8) — 1, wenn keine vermerkt ist.
 *
 * Handyfotos liegen oft quer in der Datei und tragen nur den Vermerk „um 90°
 * gedreht anzeigen“. Der Browser beachtet ihn, PDF tut es nicht: ein solches
 * JPEG darf deshalb nicht unverändert in die PDF-Hülle.
 */
export function jpegOrientierung(b: Uint8Array): number {
    if (!beginntMit(b, [0xff, 0xd8])) return 1;
    const u16 = (o: number, le = false) => (le ? b[o] | (b[o + 1] << 8) : (b[o] << 8) | b[o + 1]);
    let pos = 2;
    while (pos + 4 <= b.length) {
        if (b[pos] !== 0xff) return 1;
        const marke = b[pos + 1];
        // Ab den Bilddaten (SOS) kommt kein EXIF mehr.
        if (marke === 0xda || marke === 0xd9) return 1;
        const laenge = u16(pos + 2);
        if (laenge < 2) return 1;
        if (marke === 0xe1 && beginntMit(b, ascii("Exif\0\0"), pos + 4)) {
            const tiff = pos + 10;
            if (tiff + 8 > b.length) return 1;
            const le = b[tiff] === 0x49 && b[tiff + 1] === 0x49;
            if (!le && !(b[tiff] === 0x4d && b[tiff + 1] === 0x4d)) return 1;
            const u32 = (o: number) =>
                le
                    ? (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0
                    : ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
            const ifd = tiff + u32(tiff + 4);
            if (ifd + 2 > b.length) return 1;
            const anzahl = u16(ifd, le);
            for (let i = 0; i < anzahl; i++) {
                const eintrag = ifd + 2 + i * 12;
                if (eintrag + 12 > b.length) return 1;
                if (u16(eintrag, le) === 0x0112) {
                    const wert = u16(eintrag + 8, le);
                    return wert >= 1 && wert <= 8 ? wert : 1;
                }
            }
            return 1;
        }
        pos += 2 + laenge;
    }
    return 1;
}

/**
 * Userspace-Rechteck der Bildseite als Pixel `[x, y, breite, hoehe]` im Bild.
 * Die Seite beginnt bei (0|0) unten links; `pxJePt` = Bildbreite / Seitenbreite.
 * Nach außen gerundet und auf das Bild beschnitten — ein entfernter Bereich
 * darf keinen Pixelrand stehen lassen. `null`, wenn nichts im Bild liegt.
 */
export function rechteckImBild(
    rect: PdfRect,
    pxJePt: number,
    breitePx: number,
    hoehePx: number,
): [number, number, number, number] | null {
    const x0 = Math.max(0, Math.floor(Math.min(rect[0], rect[2]) * pxJePt));
    const x1 = Math.min(breitePx, Math.ceil(Math.max(rect[0], rect[2]) * pxJePt));
    const y0 = Math.max(0, Math.floor(hoehePx - Math.max(rect[1], rect[3]) * pxJePt));
    const y1 = Math.min(hoehePx, Math.ceil(hoehePx - Math.min(rect[1], rect[3]) * pxJePt));
    return x1 > x0 && y1 > y0 ? [x0, y0, x1 - x0, y1 - y0] : null;
}

/** Größe des Bildes nach einer Drehung um `grad` (Vielfaches von 90). */
export function gedrehteMasse(breitePx: number, hoehePx: number, grad: number): { breite: number; hoehe: number } {
    const g = ((Math.trunc(grad) % 360) + 360) % 360;
    return g === 90 || g === 270 ? { breite: hoehePx, hoehe: breitePx } : { breite: breitePx, hoehe: hoehePx };
}
